import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import memoize from 'lodash/memoize';
import humanizeUtil from '@utils/humanizeUtil';
import { SPI_METRIC } from '@components/top-widget-panel/spi-panel/widgets/constants';
import MapTooltip from './MapTooltip';
import { getTextColor } from '@src/utils/kpi/tools';

export type MarkerDataLike = Record<string, number> & {
  value: [string];
  name: [string];
  coordinate: google.maps.LatLngLiteral;
};

export const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 37.4239163, lng: -102.0947209 };

export const mapInitOptions: google.maps.MapOptions = {
  mapTypeId: 'roadmap',
  mapTypeControl: false,
  streetViewControl: false,
  scaleControl: true,
  zoomControl: true,
  fullscreenControl: true,
  zoom: 4,
  center: DEFAULT_CENTER,
  minZoom: 2,
};

const createMarkerIcon = memoize((color = '#1093F5') => {
  const size = 40;

  return {
    path: 'M27 13.714C27 21.289 16.5 32 13.5 32C10.5 32 0 21.289 0 13.714C0 6.14 6.044 0 13.5 0C20.956 0 27 6.14 27 13.714Z',
    fillColor: color,
    fillOpacity: 1,
    strokeWeight: 1,
    strokeOpacity: 0.7,
    strokeColor: '#4c4c4c',
    rotation: 0,
    scale: 1.5,
    labelOrigin: new google.maps.Point(14, 12),
    anchor: new google.maps.Point(0.31 * size, size - 5),
    origin: new google.maps.Point(0, 0),
    size: new google.maps.Size(size, size),
  };
});

function createMarkerOptions<T extends MarkerDataLike>(
  data: T,
  metricInfo: { name: string; unit: string; short: string },
  zIndex: number,
) {
  const formattedValue = humanizeUtil(data[metricInfo.name], metricInfo.name, metricInfo.unit);

  if (formattedValue.humanizedNumber === null) {
    formattedValue.humanizedNumber = 'NA';
    formattedValue.humanizedUnit = '';
  }

  const tooltipValue = `${formattedValue.humanizedNumber} ${formattedValue.humanizedUnit}`;
  // Remove the unit from the label if it's not a percentage, and make the text more compact
  const markerLabel = [
    `${formattedValue.humanizedNumber}`.trim(),
    `${formattedValue.humanizedUnit}`
      .trim()
      .replace(metricInfo.unit === '%' ? '' : metricInfo.unit, ''),
  ].join('');
  const metricColor = SPI_METRIC?.includes(metricInfo.name)
    ? getTextColor(data[metricInfo.name])
    : undefined;

  const options: google.maps.MarkerOptions = {
    position: data.coordinate,
    label: {
      text: markerLabel,
      color: '#fff',
      fontSize: '12px',
    },
    icon: createMarkerIcon(metricColor),
    animation: null,
    optimized: true,
    zIndex,
  };
  const tooltipContent = renderToStaticMarkup(
    React.createElement(MapTooltip, {
      title: data.name[0],
      metric: metricInfo.short,
      value: React.createElement('span', { style: { color: metricColor } }, tooltipValue),
    }),
  );
  const key = [data.value[0], data.name[0], metricInfo.short, tooltipValue, metricColor].join('_');

  return { options, tooltipContent, key };
}

export function createMarker<T extends MarkerDataLike>(
  data: T,
  metricInfo: { name: string; unit: string; short: string },
  zIndex: number,
  infoWindow: google.maps.InfoWindow,
  map: google.maps.Map,
) {
  const { options, tooltipContent, key } = createMarkerOptions(data, metricInfo, zIndex);
  const marker = new google.maps.Marker({
    ...options,
    map,
  });

  marker.set('id-key', key);
  marker.set('tooltip-content', tooltipContent);

  marker.addListener('mouseover', () => {
    infoWindow.setContent(marker.get('tooltip-content'));
    infoWindow.open(map, marker);
  });
  marker.addListener('mouseout', () => infoWindow.close());

  return marker;
}

export function updateMarker<T extends MarkerDataLike>(
  marker: google.maps.Marker,
  data: T,
  metricInfo: { name: string; unit: string; short: string },
  zIndex: number,
) {
  const { options, tooltipContent, key } = createMarkerOptions(data, metricInfo, zIndex);

  // If the data is same as previous, just need to update zIndex
  if (marker.get('id-key') === key) {
    marker.setZIndex(zIndex);
    return;
  }

  marker.setOptions(options);
  marker.set('id-key', key);
  marker.set('tooltip-content', tooltipContent);
}

export function deleteMarker(marker: google.maps.Marker) {
  marker.setMap(null);
  google.maps.event.clearListeners(marker, 'mouseover');
  google.maps.event.clearListeners(marker, 'mouseout');
  marker.unbindAll();
}

export function getFirstPointToMapCenter<
  T extends Array<{ coordinate: google.maps.LatLngLiteral }>,
>(data: T) {
  return data.find((it) => !!it.coordinate)?.coordinate;
}

export function getMapPointsBoundsCenter<
  T extends Array<{ coordinate: google.maps.LatLngLiteral }>,
>(data: T) {
  const bounds = new google.maps.LatLngBounds();
  data.forEach((it) => {
    bounds.extend(it.coordinate);
  });

  return bounds.getCenter();
}

export function getMapZoom(dimension: string) {
  switch (dimension) {
    case 'GEO_STATE':
    case 'geo_state':
      return 4;

    case 'GEO_CITY':
    case 'geo_city':
      return 7;

    case 'GEO_COUNTRY':
    case 'geo_country':
    default:
      return 3;
  }
}

export function diffVisiblePoints(oldPoints: string[], newPoints: string[]) {
  const oldSet = new Set(oldPoints);
  const newSet = new Set(newPoints);
  const added = [...newSet].filter((item) => !oldSet.has(item));
  const removed = [...oldSet].filter((item) => !newSet.has(item));
  const unchanged = [...newSet].filter((item) => oldSet.has(item));

  return { added, removed, unchanged };
}
